import { Box } from '@mui/material';
import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { PageHeroSlotContext } from '@components/Common/PageHeader';
// Styles live in the global layer (see src/index.scss) so a module can use
// .cc-panel without importing this component.

type PanelVariant = 'panel' | 'stat';

interface PanelProps {
  /** `panel` is a page section; `stat` is a KPI tile. */
  variant?: PanelVariant;
  className?: string;
  /**
   * Route this tile navigates to. When set the surface becomes a real link:
   * keyboard focusable, Enter and Space activate, right-click and
   * middle-click behave like any other link.
   *
   * Only pass it when the destination genuinely answers "show me these
   * records". A tile that lands on an unfiltered page teaches people the
   * click is not worth making, which is worse than no link at all.
   */
  to?: string;
  /** Accessible name for the link, e.g. "View all jobs". Required with `to`. */
  linkLabel?: string;
  children: React.ReactNode;
}

/**
 * The surface every dashboard and analytics section wants.
 *
 * It exists because four modules were reaching into AutomatedEmails for
 * `.ae-table-panel` and `.ae-summary-card`. That worked only while that
 * stylesheet happened to be bundled, and it silently carried the fill those
 * classes had at the time, which was `rgba(255, 255, 255, 0.88)` — near-white
 * glass on a graphite page. A shared component makes the dependency explicit
 * and gives it one owner.
 */
export const Panel: React.FC<PanelProps> = ({
  variant = 'panel',
  className = '',
  to,
  linkLabel,
  children,
}) => {
  const classes = `cc-panel cc-panel--${variant} ${className}`.trim();

  // A real <a> rather than a Box with onClick: that is what gives keyboard
  // activation, focus order, and open-in-new-tab for free. Faking it with a
  // click handler would need role, tabIndex and a keydown handler to match,
  // and would still lose middle-click.
  if (to) {
    return (
      <Link aria-label={linkLabel} className={`${classes} cc-panel--link`} to={to}>
        {children}
      </Link>
    );
  }

  return <Box className={classes}>{children}</Box>;
};

interface PageHeroProps {
  /** Small uppercase overline naming the section. */
  kicker: string;
  title: string;
  /** One sentence on what the page is for. Takes markup — the send log
      names its collection in <code>. */
  text?: React.ReactNode;
  /** Buttons aligned to the right of the copy. */
  actions?: React.ReactNode;
}

/**
 * The page header block shared by the email suite, Dashboard and analytics.
 *
 * Plain elements, not `Typography`: MUI's emotion classes are injected after
 * the stylesheet, so `.MuiTypography-root { margin: 0 }` beat every margin set
 * here and the three lines rendered flush against each other however much
 * leading the SCSS asked for. The variants were wrong for a hero anyway —
 * `h5` is the card-header size. The scale now lives in one place, in SCSS.
 */
export const PageHero: React.FC<PageHeroProps> = ({ kicker, title, text, actions }) => {
  /*
   * Inside a page that has a real header (Common/PageHeader with a PageHeroSlot around this tab),
   * the hero is not a card: its sentence becomes the header's subtitle and its actions sit in the
   * header's action area. A hidden tab says nothing. The kicker is dropped there, because the H1
   * and the selected tab already name the page.
   */
  const slot = useContext(PageHeroSlotContext);
  if (slot) {
    if (!slot.active) return null;
    return (
      <>
        {slot.subtitleEl &&
          createPortal(
            <>
              {title}
              {text && <span className="cc-page-header__subtitle-more"> {text}</span>}
            </>,
            slot.subtitleEl,
          )}
        {actions && slot.actionsEl && createPortal(actions, slot.actionsEl)}
      </>
    );
  }

  return (
    <Box className="cc-hero">
      <div className="cc-hero__copy">
        <span className="cc-hero__kicker">{kicker}</span>
        <h2 className="cc-hero__title">{title}</h2>
        {text && <p className="cc-hero__text">{text}</p>}
      </div>
      {actions && <div className="cc-hero__actions">{actions}</div>}
    </Box>
  );
};

export default Panel;
